import React, { createContext, useContext, useReducer, useEffect, useState, useCallback, useRef } from 'react';
import { getMe, getConfig, getEffectiveVolumeDiscounts } from '../api';

// ── Brand context ─────────────────────────────────────────────────────────────
export const BrandContext = createContext({ brandName: 'Solidiphy', brandColor: '#0077B6', brandLogo: '' });
export const useBrand = () => useContext(BrandContext);

export function BrandProvider({ children }) {
  // Load from localStorage immediately to avoid color flash on reload
  const saved = (() => { try { return JSON.parse(localStorage.getItem('solidiphy_brand') || '{}'); } catch { return {}; } })();
  const [brand, setBrand] = useState({
    brandName:  saved.brandName  || 'Solidiphy',
    brandColor: saved.brandColor || '#0077B6',
    brandLogo:  saved.brandLogo  || '',
  });

  const applyBrand = useCallback((b) => {
    const color = b.brandColor || '#0077B6';
    setBrand(b);
    // Persist to localStorage so color survives page reloads
    localStorage.setItem('solidiphy_brand', JSON.stringify(b));
    document.documentElement.style.setProperty('--brand', color);
    document.documentElement.style.setProperty('--brand-dark', adjustColor(color, -20));
    document.documentElement.style.setProperty('--brand-light', hexToRgba(color, 0.1));
    const isPSP   = window.location.pathname.startsWith('/psp');
    const isAdmin = window.location.pathname.startsWith('/admin');
    if (!isPSP && !isAdmin) {
      document.title = `${b.brandName || 'Solidiphy'} — Photographer Portal`;
    }
  }, []);

  // Apply saved brand immediately on mount
  useEffect(() => {
    const isPSP   = window.location.pathname.startsWith('/psp');
    const isAdmin = window.location.pathname.startsWith('/admin');
    // Never apply photographer brand colors on PSP or admin portals
    if (isPSP || isAdmin) return;
    if (saved.brandColor) {
      document.documentElement.style.setProperty('--brand', saved.brandColor);
      document.documentElement.style.setProperty('--brand-dark', adjustColor(saved.brandColor, -20));
      document.documentElement.style.setProperty('--brand-light', hexToRgba(saved.brandColor, 0.1));
    }
    // Only use getConfig as fallback if no company brand saved yet
    if (!saved.brandColor) {
      getConfig().then(({ data }) => applyBrand({
        brandName:  data.brandName  || 'Solidiphy',
        brandColor: data.brandColor || '#0077B6',
        brandLogo:  data.brandLogo  || '',
      })).catch(() => {});
    }
  }, []);

  return <BrandContext.Provider value={{ ...brand, applyBrand }}>{children}</BrandContext.Provider>;
}

function adjustColor(hex, amount) {
  const num = parseInt(hex.replace('#',''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
  return `#${((r<<16)|(g<<8)|b).toString(16).padStart(6,'0')}`;
}

function hexToRgba(hex, alpha) {
  const num = parseInt(hex.replace('#',''), 16);
  return `rgba(${(num>>16)&255},${(num>>8)&255},${num&255},${alpha})`;
}

// ── Auth context ──────────────────────────────────────────────────────────────
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const { applyBrand } = useBrand();
  const [auth, setAuth] = useState({ checked: false, authenticated: false, group: null, company: null, isAdmin: false });

  const refresh = useCallback(async () => {
    try {
      const { data } = await getMe();
      setAuth({ checked: true, ...data });
      const isPSP   = window.location.pathname.startsWith('/psp');
      const isAdmin = window.location.pathname.startsWith('/admin');
      // Never apply a company's brand color on the PSP or admin portals —
      // this is what was actually leaking the storefront's brand color into
      // admin's buttons/badges: this effect runs on every mount regardless of
      // route, and (being in a parent provider) fires AFTER AdminScreen's own
      // brand-reset effect, silently overwriting it.
      if (data.authenticated && data.company && !isPSP && !isAdmin) {
        applyBrand({
          brandName:  data.company.brandName  || data.company.name,
          brandColor: data.company.brandColor || '#0077B6',
          brandLogo:  data.company.brandLogo  || '',
        });
      }
    } catch {
      setAuth({ checked: true, authenticated: false });
    }
  }, [applyBrand]);

  useEffect(() => { refresh(); }, []);

  const applyCompanyBrand = useCallback((company) => {
    if (company) applyBrand({
      brandName:  company.brandName  || company.name,
      brandColor: company.brandColor || '#0077B6',
      brandLogo:  company.brandLogo  || '',
    });
  }, [applyBrand]);

  return (
    <AuthContext.Provider value={{ ...auth, refresh, applyCompanyBrand }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Cart context ──────────────────────────────────────────────────────────────
export const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const cartInit = {
  items:    [],
  customer: { firstName:'', lastName:'', email:'', phone:'' },
  address:  { firstName:'', lastName:'', address1:'', address2:'', city:'', province:'', zip:'', country:'US', phone:'' },
  shipping: { rates:[], selected: null },
  tax:      { enabled: false, rate: 0, amount: 0 },
  roomNumber: '',
  coupon:   null, // { code, type, discountPercent, discountAmount }
};

// Cart survives a page refresh (sessionStorage — clears when the browser
// tab/window closes, unlike localStorage, so an abandoned cart doesn't
// linger indefinitely on a shared photographer device). Also cleared
// explicitly on logout — see the authenticated-watching effect below —
// so one photographer's in-progress cart never bleeds into the next
// person's session on the same device.
const CART_STORAGE_KEY = 'solidiphy_cart';
function loadPersistedCart() {
  try {
    const saved = sessionStorage.getItem(CART_STORAGE_KEY);
    if (!saved) return cartInit;
    return { ...cartInit, ...JSON.parse(saved) };
  } catch { return cartInit; }
}

function cartReducer(s, a) {
  switch (a.type) {
    case 'ADD_ITEM':
      return { ...s, items: [...s.items, { ...a.item, id: Date.now().toString() }] };
    case 'UPDATE_QTY':   return { ...s, items: s.items.map(i => i.id === a.id ? { ...i, quantity: Math.max(1, a.qty) } : i) };
    case 'REMOVE_ITEM':  return { ...s, items: s.items.filter(i => i.id !== a.id) };
    case 'SET_CUSTOMER': return { ...s, customer: { ...s.customer, ...a.data } };
    case 'SET_ADDRESS':  return { ...s, address:  { ...s.address,  ...a.data } };
    case 'SET_RATES':    return { ...s, shipping: { ...s.shipping, rates: a.rates } };
    case 'SELECT_RATE':  return { ...s, shipping: { ...s.shipping, selected: a.rate } };
    case 'SET_TAX':      return { ...s, tax: a.data };
    case 'SET_ROOM':     return { ...s, roomNumber: a.roomNumber };
    case 'SET_COUPON':   return { ...s, coupon: a.coupon };
    case 'CLEAR_COUPON': return { ...s, coupon: null };
    case 'CLEAR':        return cartInit;
    default:             return s;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, undefined, loadPersistedCart);
  const [volumeTiers, setVolumeTiers] = useState({ allSkus: [], bySku: {} });
  const { authenticated, company } = useAuth();

  // Persist on every change so a refresh doesn't lose the cart.
  useEffect(() => {
    try { sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state)); } catch {}
  }, [state]);

  // Clear the persisted cart on logout — prevents one photographer's
  // in-progress cart from bleeding into the next login on a shared device.
  // Only fires on an actual true→false transition (skips the initial
  // mount, where authenticated starts false before the auth check resolves).
  const wasAuthenticated = useRef(authenticated);
  useEffect(() => {
    if (wasAuthenticated.current && !authenticated) {
      dispatch({ type: 'CLEAR' });
      try { sessionStorage.removeItem(CART_STORAGE_KEY); } catch {}
    }
    wasAuthenticated.current = authenticated;
  }, [authenticated]);

  // Fetch whenever a group session actually becomes available. CartProvider
  // sits at the top of the app and never unmounts across client-side route
  // changes (login, logout, navigating between screens), so this can NOT be
  // a one-time mount-only effect gated on the URL path — the very first
  // mount usually happens on /login (before authenticated is true), and a
  // path check alone would never re-run once the user logs in and the SPA
  // navigates without a full page reload. Re-running when `authenticated`
  // flips true (or the company changes, e.g. logging into a different
  // company without a full refresh) is what actually catches every case.
  useEffect(() => {
    if (!authenticated) { setVolumeTiers({ allSkus: [], bySku: {} }); return; }
    getEffectiveVolumeDiscounts()
      .then(({ data }) => setVolumeTiers(data))
      .catch(() => {}); // no promotions configured, or request failed — fine, just shows no discount
  }, [authenticated, company?.id]);

  // Best qualifying tier among a set of tiers (already flattened from
  // whichever promotions apply), evaluated at a given quantity — mirrors
  // services/volumePromotions.js bestTier() exactly: highest % wins, ties
  // broken by higher minQty.
  const bestTier = useCallback((tiers, quantity) => {
    if (!tiers || !tiers.length) return null;
    const candidates = tiers.filter(t => quantity >= t.minQty);
    if (!candidates.length) return null;
    const sorted = [...candidates].sort((a, b) => (b.discountPercent - a.discountPercent) || (b.minQty - a.minQty));
    return sorted[0];
  }, []);

  const subtotal     = state.items.reduce((s,i) => s + i.retailPrice * i.quantity, 0);
  const baseShipping = state.shipping.selected?.price || 0;

  // Volume discount — Phase 8 promotion model:
  //   specific_sku promotion (if one exists for this line's SKU) wins,
  //   triggered by the TOTAL quantity of that SKU across the whole cart
  //   (not just this one line, in case the same SKU was added as separate
  //   cart entries). If it doesn't qualify, that line gets NO discount —
  //   it does not fall back to the all_skus schedule.
  //   Otherwise, an all_skus promotion applies, triggered by TOTAL cart
  //   quantity (sum of every line's quantity).
  const totalCartQuantity = state.items.reduce((s,i) => s + i.quantity, 0);
  const skuQuantityTotals = state.items.reduce((acc, i) => {
    acc[i.sku] = (acc[i.sku] || 0) + i.quantity;
    return acc;
  }, {});
  const itemsWithDiscount = state.items.map(i => {
    const skuTiers = volumeTiers.bySku && volumeTiers.bySku[i.sku];
    let tier = null;
    if (skuTiers && skuTiers.length) {
      tier = bestTier(skuTiers, skuQuantityTotals[i.sku]); // may be null — does NOT fall back to allSkus
    } else {
      tier = bestTier(volumeTiers.allSkus, totalCartQuantity);
    }
    const lineTotal = i.retailPrice * i.quantity;
    const volumeDiscountPercent = tier ? tier.discountPercent : 0;
    const volumeDiscountAmount  = tier ? parseFloat((lineTotal * (tier.discountPercent / 100)).toFixed(2)) : 0;
    return { ...i, volumeDiscountPercent, volumeDiscountAmount };
  });
  const volumeDiscountTotal = parseFloat(itemsWithDiscount.reduce((s, i) => s + i.volumeDiscountAmount, 0).toFixed(2));

  // Apply coupon
  const couponDiscount = state.coupon
    ? state.coupon.type === 'free_shipping'
      ? baseShipping
      : parseFloat((subtotal * (state.coupon.discountPercent / 100)).toFixed(2))
    : 0;
  const shippingCost   = state.coupon?.type === 'free_shipping' ? 0 : baseShipping;
  // Combined discount: volume discount always, coupon only when percent_discount
  // (free_shipping coupon only affects shippingCost above, never the subtotal).
  const discountAmount = parseFloat((volumeDiscountTotal + (state.coupon?.type === 'percent_discount' ? couponDiscount : 0)).toFixed(2));
  const discountedSub  = parseFloat((subtotal - discountAmount).toFixed(2));
  const taxAmount      = state.tax.amount || 0;
  const total          = parseFloat((discountedSub + shippingCost + taxAmount).toFixed(2));
  const cartCount      = state.items.reduce((s,i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      ...state, items: itemsWithDiscount, subtotal, shippingCost, taxAmount, total, cartCount,
      couponDiscount, baseShipping, volumeDiscountTotal, discountAmount,
      addItem:      (item)       => dispatch({ type:'ADD_ITEM', item }),
      updateQty:    (id, qty)    => dispatch({ type:'UPDATE_QTY', id, qty }),
      removeItem:   (id)         => dispatch({ type:'REMOVE_ITEM', id }),
      setCustomer:  (data)       => dispatch({ type:'SET_CUSTOMER', data }),
      setAddress:   (data)       => dispatch({ type:'SET_ADDRESS', data }),
      setRates:     (rates)      => dispatch({ type:'SET_RATES', rates }),
      selectRate:   (rate)       => dispatch({ type:'SELECT_RATE', rate }),
      setTax:       (data)       => dispatch({ type:'SET_TAX', data }),
      setRoomNumber:(roomNumber) => dispatch({ type:'SET_ROOM', roomNumber }),
      setCoupon:    (coupon)     => dispatch({ type:'SET_COUPON', coupon }),
      clearCoupon:  ()           => dispatch({ type:'CLEAR_COUPON' }),
      clearCart:    ()           => dispatch({ type:'CLEAR' }),
    }}>
      {children}
    </CartContext.Provider>
  );
}
